/**
 * Web twin of the native highlight overlay on PhotoMapViewer / ReviewStep.
 *
 * - `drawingPaths` (mobile capture): same as PhotoMapViewer — no viewBox,
 *   1:1 CSS-pixel mapping into the 3:4 photo box (paths were authored in
 *   that canvas space on the capture device).
 * - `highlightSvg` (web capture): self-contained SVG with its own viewBox;
 *   stretch to the photo box like ReviewStep (`[&>svg]:size-full`).
 */
import { useMemo } from 'react';

const HIGHLIGHT_STROKE_WIDTH = 35;
const HIGHLIGHT_STROKE_COLOR = 'rgba(255, 20, 147, 0.8)';

export type DrawingPoint = { x: number; y: number };
export type DrawingPath = DrawingPoint[];

interface FlatPath {
  pathIndex: number;
  points: Array<{ pointIndex: number; x: number; y: number }>;
}

interface Props {
  highlightSvg?: string | null;
  drawingPaths?: unknown;
  className?: string;
}

function isFlatPaths(paths: unknown[]): paths is FlatPath[] {
  const first = paths[0];
  return (
    !!first &&
    typeof first === 'object' &&
    first !== null &&
    'pathIndex' in first &&
    'points' in first
  );
}

function normalizeDrawingPaths(raw: unknown): DrawingPath[] {
  if (!raw) return [];
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return [];

  if (isFlatPaths(parsed)) {
    return [...parsed]
      .sort((a, b) => a.pathIndex - b.pathIndex)
      .map((pathData) =>
        [...(pathData.points || [])]
          .sort((a, b) => a.pointIndex - b.pointIndex)
          .map((p) => ({ x: p.x, y: p.y })),
      )
      .filter((path) => path.length > 0);
  }

  return parsed
    .map((path) => {
      if (!Array.isArray(path)) return [];
      return path
        .filter(
          (p): p is DrawingPoint =>
            !!p &&
            typeof p === 'object' &&
            typeof (p as DrawingPoint).x === 'number' &&
            typeof (p as DrawingPoint).y === 'number',
        )
        .map((p) => ({ x: p.x, y: p.y }));
    })
    .filter((path) => path.length > 0);
}

function pathToD(points: DrawingPath): string {
  if (points.length === 0) return '';
  const [first, ...rest] = points;
  return rest.reduce((acc, p) => `${acc} L ${p.x} ${p.y}`, `M ${first.x} ${first.y}`);
}

export default function TaskHighlightOverlay({
  highlightSvg,
  drawingPaths,
  className = '',
}: Props) {
  const paths = useMemo(() => normalizeDrawingPaths(drawingPaths), [drawingPaths]);
  const svgMarkup = typeof highlightSvg === 'string' ? highlightSvg.trim() : '';

  if (paths.length > 0) {
    // Match apps/mobile PhotoMapViewer: no viewBox — path units = CSS px
    // of the photo box (capture canvas was the same 3:4 full-width frame).
    return (
      <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
        <svg className="absolute inset-0 size-full overflow-visible">
          {paths.map((path, i) => (
            <path
              key={i}
              d={pathToD(path)}
              stroke={HIGHLIGHT_STROKE_COLOR}
              strokeWidth={HIGHLIGHT_STROKE_WIDTH}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      </div>
    );
  }

  if (svgMarkup) {
    // HighlightStep draws with preserveAspectRatio="none" over object-cover
    // photo — playback must do the same or the scribble drifts.
    const markup = /preserveAspectRatio=/i.test(svgMarkup)
      ? svgMarkup
      : svgMarkup.replace('<svg ', '<svg preserveAspectRatio="none" ');
    return (
      <div
        className={`pointer-events-none absolute inset-0 [&>svg]:size-full ${className}`}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: markup }}
      />
    );
  }

  return null;
}

export function hasHighlightContent(
  highlightSvg?: string | null,
  drawingPaths?: unknown,
  hasOverlay?: boolean,
): boolean {
  const hasSvg = typeof highlightSvg === 'string' && highlightSvg.trim().length > 0;
  const hasPaths = normalizeDrawingPaths(drawingPaths).length > 0;
  if (!hasSvg && !hasPaths) return false;
  return hasOverlay === true || hasSvg;
}
